/**
 * ProcessActionButton — przycisk akceptacji rozmieszczany w formularzu rekordu (element kreatora
 * „Przycisk akceptacji”, `process_action_button`).
 *
 * Odwzorowuje JEDNO miejsce paska akceptacji nad zakładkami (`ProcessApprovalBanner`), w tym samym
 * modelu „slotu” rekordu (`resolveRecordProcessSlots`) i z tą samą listą spraw (klucz zapytania
 * `recordInstancesKey`, więc react-query nie dubluje żądania z paskiem):
 *   - slot WOLNY (brak sprawy poza anulowanymi) → `SubmitForApprovalButton` („Wyślij do
 *     akceptacji”; sam się ukrywa bez prawa startu albo bez pasującego procesu), z tym samym typem
 *     dokumentu / kategorią i bramką zapisu co pasek;
 *   - sprawa ŻYWA (w toku / zawieszona) → tylko „Akceptacja” (`ApprovalActionsPanel` z
 *     `primaryOnly`: bez przycisków dróg i menu „…” — drogę wybiera się w oknie); panel sam znika,
 *     gdy zalogowany użytkownik nie może teraz decydować;
 *   - inaczej (np. sprawa zakończona) → nic. Bez „Anuluj proces”.
 * Wczytywanie i błąd listy → nic (cicho; komunikat błędu pokazuje pasek).
 */

import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  recordInstancesKey,
  resolveRecordProcessSlots,
  selectRecordInstanceRows,
} from '@/hooks/useActiveInstanceForRecord';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';
import { fetchInstancesForRecord } from '@/lib/processes/instance-api';

import { ApprovalActionsPanel } from './ApprovalActionsPanel';
import { SubmitForApprovalButton } from './SubmitForApprovalButton';

export interface ProcessActionButtonProps {
  moduleCode: string;
  recordId: string;
  /** Typ bieżącego rekordu (typ dokumentu, typ zadania) — do wyboru procesu przy starcie. */
  typeId?: string | null;
  /** Kategoria bieżącego rekordu (kontrahenci, moduły własne) — do wyboru procesu przy starcie. */
  categoryId?: string | null;
  /**
   * Bramka przed otwarciem okna (formularz w edycji: najpierw zapis, potem decyzja) — ta sama, którą
   * strona daje paskowi akceptacji; trafia do „Akceptacji” oraz (slot wolny) do „Wyślij do
   * akceptacji”. Brak = okno otwiera się od razu.
   */
  beforeDecision?: DecisionGate;
  testIdPrefix?: string;
}

export function ProcessActionButton({
  moduleCode,
  recordId,
  typeId,
  categoryId,
  beforeDecision,
  testIdPrefix = 'process-action-button',
}: ProcessActionButtonProps): ReactElement | null {
  const listQuery = useQuery({
    queryKey: recordInstancesKey(moduleCode, recordId),
    queryFn: () => fetchInstancesForRecord(moduleCode, recordId),
    enabled: !!moduleCode && !!recordId,
    select: selectRecordInstanceRows,
  });

  if (listQuery.isPending || listQuery.isError) return null;

  const { live, free } = resolveRecordProcessSlots(listQuery.data ?? []);

  if (live !== null) {
    return (
      <ApprovalActionsPanel
        moduleCode={moduleCode}
        recordId={recordId}
        beforeDecision={beforeDecision}
        primaryOnly
        testIdPrefix={`${testIdPrefix}-decision`}
      />
    );
  }

  if (free) {
    return (
      <SubmitForApprovalButton
        moduleCode={moduleCode}
        recordId={recordId}
        typeId={typeId}
        categoryId={categoryId}
        beforeSubmit={beforeDecision}
        testIdPrefix={`${testIdPrefix}-submit`}
      />
    );
  }

  return null;
}
